/**
 * Transparent (Editor tab) — bulk magenta-to-transparent converter with live scores.
 *
 * Pick a folder of magenta base renders; each file is chroma-keyed (src/transparent/keying.ts,
 * the in-app twin of scripts/transparent_overlay.py), square-normalized, and scored:
 *
 *   case score  non-magenta pixels byte-identical to the source (100% by construction)
 *   roundtrip   composite the result back over the flat magenta -> compare with the source
 *
 * Desktop writes the mirrored tree to a picked folder; web falls back to a zip download.
 * Spec (canvas size, case height, defaults): docs/transparent-overlays.md.
 *
 * WS-N: picking a folder ENQUEUES a queue job — it no longer runs a `for` loop inside a React
 * component. The conversion itself is `transparent/run.ts`, reached through the manager's
 * `runTransparent` port, so this tab and the Queue tab are two views of one run: pause, resume,
 * retry-failed and the per-file error log all come from `QueueManager` rather than from a
 * `useRef(false)` this component owned. The table below renders the live rows the port mirrors
 * into `useQueueStore.transparentRows`.
 */
import { useEffect, useRef, useState } from "react";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import { RowAction } from "../ui/RowAction.js";
import { ActionBar } from "../ui/ActionBar.js";
import { Accordion } from "../ui/Accordion.js";
import { useBatchStore, runFolderName } from "../stores/batch.js";
import { useQueueStore } from "../stores/queue.js";
import {
  destLabel, exportProblemSummary, TRANSPARENT_BUNDLE_ROOT,
} from "../export/bundle.js";
import { runProblemNotice } from "../export/runProblems.js";
import { copyOverlaysTo } from "../export/copyRun.js";
import {
  SHIPPED_KEYING, SHIPPED_LAYOUT,
  type KeyingConfig, type LayoutConfig,
} from "./keying.js";
// WS-N: the conversion and the save live in `run.ts` so the QUEUE can run the same code this
// panel runs. This component keeps its table and its controls; it owns no per-file loop.
import {
  brandFolder, IMAGE_RE, savesWhenTheJobFinishes, saveTransparentRows, savedMessage,
  type TransparentRow,
} from "./run.js";
import "./transparent.css";

/** Derived from the QUEUE job's status now, not from a local flag this component sets. */
type Phase = "idle" | "working" | "done";

/** A STABLE empty array for the "no rows yet" selector. Returning a fresh `[]` from a zustand
 *  selector is a new reference every render, which is an infinite re-render loop. */
const NO_ROWS: TransparentRow[] = [];

export function TransparentPanel() {
  const { io, client, logger, queue } = useServices();
  /** WS-N review-1 M-4: desktop's job files the overlays itself; web waits for the click. */
  const savesOnFinish = savesWhenTheJobFinishes(io.kind);
  const [keying, setKeying] = useState<KeyingConfig>({ ...SHIPPED_KEYING });
  const [layout, setLayout] = useState<LayoutConfig>({ ...SHIPPED_LAYOUT });
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  /**
   * WS-O / O-11 (LB-2): where this run actually wrote, stamped by its FIRST overlay rather than
   * by the end of the job (`app/bootstrap.ts::onOutput`). It is what both verbs stand on — never
   * a write COUNT, because a run whose every write was refused is exactly when the operator most
   * needs to open the (empty) folder and see why.
   */
  const lastSavedPath = useBatchStore((s) => s.lastSavedPath);
  /**
   * WS-T: the folder this tab's conversions write into — the SAME remembered folder the mockup
   * export uses, not a Transparent-only one.
   *
   * The overlays already land beside the mockups under one operator-chosen root
   * (`TRANSPARENT_BUNDLE_ROOT` is a sibling of `MOCKUPS_BUNDLE_ROOT`), and the destination note
   * at the bottom of this panel says so in as many words. A second key would make that sentence
   * false and hand the operator two folders where the app promises one. It persists device-
   * locally through `setSaveDir` (`stores/batch.ts`'s `SAVE_DIR_KEY`) and is rehydrated at boot
   * by `restoreSaveDir`, so "saved by default until it is changed" spans a relaunch.
   */
  const saveDir = useBatchStore((s) => s.saveDir);
  const setSaveDir = useBatchStore((s) => s.setSaveDir);
  /** A failed reveal. Its own row, not a sentence the next status line erases. */
  const [openErr, setOpenErr] = useState<string | null>(null);
  /** The folder "Open folder" opens, in one place so the row's icon and the summary's word can
   *  never point at two different folders: the RUN folder once there is one, the chosen folder
   *  before that (which is what this row is naming). */
  const openTarget = lastSavedPath ?? saveDir;
  /** Clear the last failure, then try. A rejection is the `transparent-open-error` row below. */
  const openFolder = (path: string): void => {
    setOpenErr(null);
    void io.openPath?.(path).catch((e: unknown) =>
      setOpenErr(e instanceof Error ? e.message : String(e)));
  };
  /** WS-Z0 F-6: the settings section's open key is OWNED HERE, not by the primitive. This
   *  panel re-renders on every queue progress tick, so the one state the operator is reading
   *  mid-run is exactly the one an uncontrolled section could lose — and the controlled pair
   *  is the primitive's own answer to that (`defaultOpen` is a seed, read once). `null` is a
   *  legitimate resting state: closed is what "shipped defaults" means. */
  const [settingsOpen, setSettingsOpen] = useState<string | null>(null);
  /**
   * Brand the converted files are filed under: pick an existing brand or type a new name — it
   * becomes the top-level output folder. Empty = keep the source layout.
   *
   * WS-N moved this control ABOVE the run. The queue job SAVES when it finishes (that is what
   * makes a queued conversion produce files without anyone watching the tab), so the brand has
   * to be part of the spec at enqueue time. Choosing it afterwards would have written the run
   * twice — once at the root and once under a brand folder.
   */
  const [brand, setBrand] = useState("");
  const [brandNames, setBrandNames] = useState<string[]>([]);
  useEffect(() => {
    let gone = false;
    void client.listBrands?.().then((rows) => {
      if (!gone) setBrandNames((rows ?? []).map((b) => b.name).filter(Boolean));
    }).catch(() => {/* offline/demo — free text still works */});
    return () => { gone = true; };
  }, [client]);

  /**
   * This tab's job — one at a time: the tab shows the run it started — and that conversion's
   * dated output folder, stamped when the run is ENQUEUED (WS-I / D-13, the same contract the
   * mockup export got in WS-H1). Stamping it once is what makes one conversion write one folder
   * — and a second conversion NOT overwrite the first. "Replace previous run" in the Render
   * card is honoured here too: it means one canonical folder, on every save surface.
   *
   * Both live in the QUEUE STORE, not in component state (WS-N review-1 M-3): `app/Editor.tsx`
   * unmounts this panel on a tab switch, so component state made the claim below false — the
   * operator came back to an idle tab over a run that was still going.
   */
  const run = useQueueStore((s) => s.transparentRun);
  const setRun = useQueueStore((s) => s.setTransparentRun);
  const jobId = run?.jobId ?? null;
  const runFolder = run?.runFolder;
  /** review-1 m-2: the brand the RUN was enqueued with — what the writer actually prefixed. */
  const runBrandFolder = run?.brandFolder;
  // The live rows the `runTransparent` port mirrors in, and the job's own status. Both come
  // from the queue, so this tab keeps painting even if the operator navigates away and back.
  const rows = useQueueStore((s) => (jobId ? s.transparentRows[jobId] ?? NO_ROWS : NO_ROWS));
  // Select the STATUS, not the JobRecord. `QueueManager` mutates the record in place and then
  // emits a new ARRAY of the same objects, so a selector returning the record itself is
  // reference-equal on every emit and zustand never re-renders — the tab would sit on
  // "Working…" for a job that finished. A primitive changes value, so it cannot go stale.
  const status = useQueueStore((s) => s.jobs.find((j) => j.jobId === jobId)?.status);
  const live = useQueueStore((s) => (jobId ? s.live[jobId] : undefined));
  /**
   * WS-N review-1 I-1: the whole-JOB sentence — "Paused — kept 2 overlays; Resume continues
   * with the rest.", "Canceled — kept …", or a conversion this build cannot run. It used to be
   * written into `useBatchStore.jobError`, i.e. the BATCH tab's error banner for a mockup run
   * that was never touched. It belongs here.
   */
  const jobNote = useQueueStore((s) => (jobId ? s.transparentNote[jobId] : undefined));
  /**
   * WS-O review-1 B-1: the overlays this conversion could NOT file.
   *
   * `Apple/Rose.png` then `Apple/rose.png` is ONE file on APFS/NTFS: the second is correctly
   * refused and named in the Error log, and this tab showed both rows `ok` with a roundtrip score
   * and "2 / 2 converted". The row state is honest (the keying DID succeed), which is exactly why
   * the tally has to be said out loud here — at WS-N the operator read it in the status line
   * after "Save all", and that verb is gone.
   */
  const runReport = useQueueStore((s) => (jobId ? s.transparentReport[jobId] : undefined));
  const phase: Phase =
    status === "running" || status === "queued" || status === "paused" ? "working"
      : status ? "done" : "idle";
  const progress = live && phase === "working" ? { i: live.completed, n: live.total } : null;

  /**
   * WS-T: can this tab write folders, and does it need to ask where?
   *
   * Feature-detected the way `BatchPanel` detects the same pair — a browser has no folders, so
   * `web.ts` ships no `pickSaveDirectory` and this whole surface is absent there.
   */
  const canPickFolder = io.kind === "desktop" && !!io.pickSaveDirectory;
  /** Reentrancy guard: a second concurrent call (the Change… button pressed while `pickAndRun`
   *  is already opening one) is a no-op rather than two native dialogs racing to set the
   *  folder. The Batch panel's `saveDirPickInFlight`, for the same reason. */
  const folderPickInFlight = useRef(false);

  /**
   * Open the folder dialog ONCE and remember what comes back. `null` means the operator
   * dismissed it (or a pick is already in flight) — the caller decides what that cancels.
   */
  async function chooseSaveDir(): Promise<string | null> {
    if (folderPickInFlight.current || !io.pickSaveDirectory) return null;
    folderPickInFlight.current = true;
    try {
      const dir = await io.pickSaveDirectory();
      if (!dir) return null;
      setSaveDir(dir); // device-local; survives restarts, never synced
      logger?.breadcrumb?.("action", "Transparent · save folder chosen", { dir });
      return dir;
    } catch (e) {
      logger?.log("error", "Transparent · save-folder dialog failed", { error: e });
      setSaveMsg(`Couldn't open the folder dialog: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    } finally {
      folderPickInFlight.current = false;
    }
  }

  async function pickAndRun() {
    setSaveMsg(null);
    setOpenErr(null);
    const files = await io.pickDirectory({ accept: ["image/png", "image/jpeg", "image/webp"] });
    const images = files.filter((f) => IMAGE_RE.test(f.name) && !f.name.startsWith("."));
    if (images.length === 0) { setSaveMsg("No images found in that folder."); return; }
    /**
     * WS-T — ONE folder question per RUN, asked here, before anything is enqueued.
     *
     * Without this the run was enqueued with `destDir: undefined`, WS-O's write-through writer
     * called `exportBundle` once per overlay, and `desktop.ts`'s
     * `bundle.destDir ?? bridge.pickDirectory()` raised a native dialog for EVERY IMAGE. The
     * mockup arm never showed it because `BatchPanel`'s `enqueue` has asked this question
     * before the run armed since D-13; this is that gate, on the tab that never got it.
     */
    let destDir = useBatchStore.getState().saveDir;
    if (canPickFolder && !destDir) {
      destDir = (await chooseSaveDir()) ?? undefined;
      if (!destDir) {
        // Cancelled BEFORE the job exists, so nothing converted and nothing was written — the
        // same sentence "Copy run to…" uses for the same refusal.
        setSaveMsg("Cancelled — no folder chosen, nothing was written.");
        return;
      }
    }
    const folder = useBatchStore.getState().replacePreviousRun ? undefined : runFolderName();
    const id = queue.enqueue({
      kind: "transparent",
      templateName: "Transparent overlays",
      spec: {
        files: images,
        keying,
        layout,
        brandFolder: brandFolder(brand),
        runFolder: folder,
        // WS-T: chosen ABOVE if it was not already remembered, so the write-through writer
        // never hands the platform a destination-less bundle.
        destDir,
      },
    });
    setRun({
      jobId: id,
      ...(folder ? { runFolder: folder } : {}),
      // review-1 m-2: the brand is part of the RUN from here on. "Copy run to…" rebuilds the
      // written paths from it, so retyping the field for the next run cannot make this one's
      // re-reads miss.
      brandFolder: brandFolder(brand),
    });
  }

  /**
   * WEB ONLY. On desktop this was "Save all (N)" — a re-file of rows the run had already
   * written, which is the same no-op "Save to folder" had become on the Batch panel, and it is
   * gone (WS-O / O-11, AUD-8 LB-3 verb #6 of six). In a browser there is no folder and the job
   * deliberately writes nothing (WS-N review-1 M-4: a zip download fired from job completion has
   * no user gesture behind it), so here this click IS the save and the label says so.
   */
  async function saveAll() {
    const savedDir = useBatchStore.getState().saveDir;
    const report = await saveTransparentRows(io, logger, rows, {
      // review-2 m-11: the RUN's brand, stamped at enqueue — the same rule the copy verb
      // follows (m-2). Reading the live field folders an `Apple/` run as `Nike/` the moment
      // the operator lines up the next one.
      brandFolder: runBrandFolder ?? brandFolder(brand),
      runFolder,
      destDir: savedDir,
    });
    if (!report) { setSaveMsg("Nothing to save."); return; }
    if (report.cancelled) {
      setSaveMsg("Save cancelled — nothing was written.");
      return;
    }
    useBatchStore.getState().setLastSavedPath(report.destPath);
    setSaveMsg(savedMessage(report, io.kind, savedDir));
  }

  /**
   * WS-O / O-11 (AUD-8 LB-3): verb two of two — the ONE re-export left, and the only one that
   * can still be pressed by hand. It picks a NEW folder and copies the run's overlays there
   * from DISK, byte-for-byte, so it works on a run whose bytes are long gone from the heap. The
   * same verb, the same string and the same refusal as the Batch panel's.
   */
  async function copyRunToFolder() {
    setSaveMsg(null);
    try {
      const srcRoot = useBatchStore.getState().lastSavedPath;
      if (!srcRoot) { setSaveMsg("Nothing to copy yet — this run hasn't written anything."); return; }
      if (!io.pickSaveDirectory) return;
      const destDir = await io.pickSaveDirectory();
      if (!destDir) { setSaveMsg("Copy cancelled — no folder chosen, nothing was written."); return; }
      // review-1 m-2: the RUN's brand, stamped at enqueue — never the field's current value.
      const folder = runBrandFolder ?? "";
      const report = await copyOverlaysTo(io, {
        srcRoot,
        destDir,
        // The paths the run WROTE — brand prefix included, exactly as the writer prefixed them.
        relPaths: rows
          .filter((r) => r.status === "ok")
          .map((r) => (folder ? `${folder}/${r.relPath}` : r.relPath)),
        logger, // write failures / path collisions belong in the Error log, not just here
      });
      setSaveMsg(
        `Copied ${report.written} file${report.written === 1 ? "" : "s"} ` +
        `${destLabel(report, io.kind, destDir)}.${exportProblemSummary(report)}`,
      );
    } catch (e) {
      logger?.log("error", "Transparent · copy run failed", { error: e });
      setSaveMsg(`Copy failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  const ok = rows.filter((r) => r.status === "ok");
  const failed = rows.filter((r) => r.status === "fail");
  const minRt = ok.length ? Math.min(...ok.map((r) => r.roundtrip ?? 0)) : 0;
  const medRt = ok.length
    ? [...ok].sort((a, b) => (a.roundtrip ?? 0) - (b.roundtrip ?? 0))[ok.length >> 1].roundtrip ?? 0
    : 0;

  const num = (v: number, d = 2) => v.toFixed(d);

  return (
    <div className="transparent-panel" data-testid="transparent-panel">
      {/* R11 (Direction B): this surface had NO heading at all — the third corner of the
          three-idiom split, and the one whose absence left the Editor's `<h1>` ("Editor")
          as the only thing naming the page. `.panel-title` is the survivor; it is an `<h2>`
          because `app/Editor.tsx` owns the route's one `<h1>`. */}
      <header className="transparent__head">
        <h2 className="panel-title">Transparent overlays</h2>
      </header>
      <p className="muted transparent__lede">
        Converts magenta base renders into transparent overlays: the panel becomes transparency,
        everything else stays byte-identical to the source. Output is a{" "}
        {layout.canvas}&times;{layout.canvas} square with the case at{" "}
        {Math.round(layout.caseHeight * 100)}% height on every model.
        <InfoHint text="The same chroma key as scripts/transparent_overlay.py, running locally. The print guide is the reference: case score 100% means no non-magenta pixel changed; roundtrip 100% means compositing the result back over flat magenta reproduces your file exactly." />
      </p>

      {/* WS-T: the destination, VISIBLE before the run rather than discovered through a native
          dialog per image. Desktop only — a browser has no folders, so `web.ts` ships no
          `pickSaveDirectory` and this row is absent there, the same gate `BatchPanel` uses for
          its own save-folder row. The path shown is the mockups' remembered folder, because
          that is where these overlays land (see the destination note below). */}
      {canPickFolder && (
        <div className="transparent__savedir" data-testid="transparent-savedir">
          <span className="transparent__savedir-label">Save to folder</span>
          <span
            className="transparent__savedir-path faint"
            data-testid="transparent-savedir-path"
            title={saveDir ?? undefined}
          >
            {saveDir ?? "Not chosen yet — you'll be asked once when you convert"}
          </span>
          {/* The owner: "Give an option to directly open the folder — if I click the folder
              image, or there should be an option called Open folder." This row named the
              destination and gave no way to reach it. Rule 13 shape — an icon, its name in a
              tooltip, no word in the row — and it opens the RUN folder once the conversion has
              one, the chosen folder before that. */}
          {io.openPath && openTarget && (
            <RowAction
              label="Open folder"
              icon="folder"
              tone="quiet"
              onClick={() => openFolder(openTarget)}
              data-testid="transparent-savedir-open"
            />
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void chooseSaveDir()}
            data-testid="transparent-savedir-choose"
          >
            {saveDir ? "Change folder…" : "Choose folder…"}
          </button>
          {/* Forgetting the folder is the operator's, not a thing they have to find in
              Settings: it deletes the persisted key, so the next conversion asks once again. */}
          {saveDir && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setSaveDir(undefined)}
              data-testid="transparent-savedir-clear"
              title="Forget this folder — the next conversion will ask for one."
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Rule 7: the conversion's commit, in the page's one `.actionbar`. Everything this row
          already held is still in it — the Brand mode field, the live progress, Stop, Open
          folder, Copy run to…, the web Download and the status message — and `ActionBar`
          places the ONE primary (the convert verb) last and REPORTS a second in DEV, without
          ever throwing (fix round 1, F-5 in `ui/ActionBar.tsx`); `B4` is the enforcement.
          The web Download keeps its label and its testid but is no longer a SECOND primary:
          a surface has one, and on this surface the commit is the conversion. */}
      <ActionBar label="Conversion controls" primary={
        <button type="button" className="btn btn--primary" onClick={() => void pickAndRun()}
          disabled={phase === "working"} data-testid="transparent-pick">
          {phase === "working" ? "Converting…" : "Pick folder & convert"}
        </button>
      }>
        {/* WS-N: the Brand field is chosen BEFORE the run and stays visible after it. The queue
            job SAVES when it finishes — that is what makes a queued conversion produce files
            without anyone watching this tab — so the brand has to be part of the spec at enqueue
            time. Picking it afterwards would have written the run twice: once at the root and
            once under a brand folder.
            K2: the inline `display/gap/fontSize/width/height` objects this label and its input
            carried are `.transparent__mode` and the one input spec — the field is the same
            field, said in CSS. */}
        <label className="transparent__mode">
          Brand
          <input
            className="input"
            list="transparent-brands"
            placeholder="pick or type…"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            disabled={phase === "working"}
            data-testid="transparent-brand"
            title="File the converted overlays under this brand folder. Pick an existing brand or type a new name; leave empty to keep the source folder layout. Choose it before converting — the queue job files the overlays as soon as it finishes."
          />
          <datalist id="transparent-brands">
            {brandNames.map((n) => <option key={n} value={n} />)}
          </datalist>
        </label>
        {phase === "working" && (
          <>
            <span className="muted" data-testid="transparent-progress">
              {progress ? `${progress.i} / ${progress.n}` : ""}
            </span>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => { if (jobId) queue.cancel(jobId); }}
              data-testid="transparent-stop"
            >
              Stop
            </button>
          </>
        )}
        {/* WS-O / O-11 (AUD-8 LB-3) — verb one of two. NOT gated on `phase === "done"`: the
            run writes each overlay as it converts, so the folder is real from overlay 1 and the
            operator can watch it fill instead of waiting for the job. Same string as the Batch
            panel's and the Queue's batch-details control, so it is learned once. */}
        {savesOnFinish && io.openPath && lastSavedPath && ok.length > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => openFolder(lastSavedPath)}
            data-testid="transparent-open-folder"
          >
            Open folder
          </button>
        )}
        {/* Verb two of two. "Save all (N)" is gone on desktop — the overlays are already in that
            folder, so the only honest re-export is a SECOND destination. */}
        {savesOnFinish && io.pickSaveDirectory && lastSavedPath && ok.length > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void copyRunToFolder()}
            data-testid="transparent-copy-run"
          >
            Copy run to…
          </button>
        )}
        {/* WEB: the job did NOT save (review-1 M-4 — a zip download needs a user gesture), so
            this click IS the save, and the label says so. */}
        {phase === "done" && !savesOnFinish && ok.length > 0 && (
          <button type="button" className="btn btn--sm" onClick={() => void saveAll()} data-testid="transparent-save">
            {`Download (${ok.length} file${ok.length === 1 ? "" : "s"})`}
          </button>
        )}
        {saveMsg && <span className="muted">{saveMsg}</span>}
      </ActionBar>

      {runProblemNotice(runReport) && (
        <div
          className="muted transparent__note transparent__note--path"
          role="status"
          data-testid="transparent-run-problems"
        >
          {/* review-2 F-1: `badge--wrap` + a title, the same pair the Batch arm carries. */}
          <span className={runProblemNotice(runReport)!.cls} title={runProblemNotice(runReport)!.text}>
            {runProblemNotice(runReport)!.text}
          </span>
        </div>
      )}
      {savesOnFinish && lastSavedPath && ok.length > 0 && (
        <small className="faint transparent__note" data-testid="transparent-dest-path">
          Written to <code>{lastSavedPath}</code>
        </small>
      )}
      {/* A failed reveal is its OWN row with the reason, beside the button that produced it —
          not a parenthetical on a status line the next message overwrites (WS-O / LB-2). It
          does not reach for `batch.css`, which only loads with the Batch panel — it uses this
          panel's own `transparent__note`, the name WS-K2 gave this exact idiom (F-61/G-12). */}
      {openErr && (
        <div className="muted transparent__note transparent__note--path" role="status" data-testid="transparent-open-error">
          Couldn&apos;t open <code>{lastSavedPath}</code>: {openErr}
        </div>
      )}

      {jobNote && (
        <div className="muted transparent__note" role="status" data-testid="transparent-job-note">
          {jobNote}
        </div>
      )}

      {phase === "done" && ok.length > 0 && (
        <small className="faint transparent__note" data-testid="transparent-dest-note">
          {savesOnFinish ? (
            <>
              Overlays land in <code>{TRANSPARENT_BUNDLE_ROOT}/{runFolder ?? "…"}/</code>{" "}
              under the same save folder as your mockups. &ldquo;Replace previous run&rdquo; in the
              Render card applies here too — with it on, every conversion writes into one canonical{" "}
              <code>{TRANSPARENT_BUNDLE_ROOT}/</code> folder instead of its own dated one.
            </>
          ) : (
            <>
              In the browser the overlays come down as a zip —{" "}
              <code>{TRANSPARENT_BUNDLE_ROOT} {runFolder ?? "…"}.zip</code> — when you press
              Download. Nothing is written until you do.
            </>
          )}
        </small>
      )}

      {/* R12: `transparent__settings-summary` was the fourth `<details>` idiom in the tree,
          each drawing its own `content: "▸"` marker. The shared `.accordion` is the survivor —
          real `<details name>` markup, the icon set's chevron as a `mask-image`, and closed by
          default, which is what "shipped defaults" means: you open it to disagree with them. */}
      <Accordion
        name="transparent-settings"
        openKey={settingsOpen}
        onOpenChange={setSettingsOpen}
        items={[{
          key: "settings",
          head: "Settings (shipped defaults)",
          body: (
            <div className="transparent__settings-row">
              {([
                ["Noise floor", "noiseFloor", 0, 20, 1],
                ["Transparent snap", "snapLow", 0, 0.2, 0.005],
                ["Opaque snap", "snapHigh", 0.8, 1, 0.005],
                ["Flat sample", "strongFrac", 0.3, 0.9, 0.05],
              ] as const).map(([label, k2, mn, mx, st]) => (
                <label key={k2} className="transparent__field">
                  {label}
                  <input type="number" min={mn} max={mx} step={st} value={keying[k2]}
                    onChange={(e) => setKeying({ ...keying, [k2]: Number(e.target.value) })}
                    className="input" />
                </label>
              ))}
              <label className="transparent__field">
                Canvas (px)
                <input type="number" min={512} max={4096} step={64} value={layout.canvas}
                  onChange={(e) => setLayout({ ...layout, canvas: Number(e.target.value) })}
                  className="input" />
              </label>
              <label className="transparent__field">
                Case height
                <input type="number" min={0.5} max={1} step={0.01} value={layout.caseHeight}
                  onChange={(e) => setLayout({ ...layout, caseHeight: Number(e.target.value) })}
                  className="input" />
              </label>
            </div>
          ),
        }]}
      />

      {rows.length > 0 && (
        <>
          <div className="transparent__stats" data-testid="transparent-stats">
            <Stat label="Converted" value={`${ok.length} / ${rows.length}`} />
            <Stat label="Case & island" value="100.00%" />
            <Stat label="Roundtrip (min)" value={ok.length ? `${num(minRt)}%` : "—"} />
            <Stat label="Roundtrip (median)" value={ok.length ? `${num(medRt)}%` : "—"} />
            <Stat label="Failed" value={String(failed.length)} />
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr><th>File</th><th>Roundtrip</th><th>Panel</th><th>Clear</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.source}>
                    <td>{r.relPath}</td>
                    <td>{r.roundtrip !== undefined ? `${num(r.roundtrip)}%` : "—"}</td>
                    <td>{r.panelShare !== undefined ? `${num(r.panelShare * 100, 1)}%` : "—"}</td>
                    <td>{r.clearShare !== undefined ? `${num(r.clearShare * 100, 0)}%` : "—"}</td>
                    <td>{r.status}{r.note ? ` — ${r.note}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat(props: { label: string; value: string }) {
  return (
    <div className="transparent__stat">
      {/* Was an inline 11px UPPERCASE eyebrow with .08em tracking — the sixteenth instance of
          the rule K2-9 deleted, invisible to a CSS-only sweep because it lived in TSX. */}
      <div className="muted transparent__stat-label">{props.label}</div>
      <div className="transparent__stat-value">{props.value}</div>
    </div>
  );
}
